import { forwardRef } from "react";

import { Input } from "antd";
import { type TextAreaProps } from "antd/lib/input";
import { type TextAreaRef } from "antd/lib/input/TextArea";

import { cn } from "@btnet/utilities-core";

import "./BuiTextArea.less";

/**
 * IBuiTextAreaProps is the interface for BuiTextArea props.
 * Extends antd's TextAreaProps; declared as its own type so BuiTextArea-specific
 * styling props can be added without breaking consumers.
 */
export interface IBuiTextAreaProps extends TextAreaProps {
  "data-testid"?: string; // Optional prop for testing purposes
}

/**
 * BuiTextArea component - a styled wrapper around antd Input.TextArea.
 * Applies the `bui-text-area` class for BDS-themed styling and forwards a ref to
 * the underlying antd TextArea. All antd TextAreaProps are supported and passed
 * through. Behavior (debouncing, character counting, read-only display, tracking)
 * lives in the app-level BTTextArea; this component is styling only.
 * @param props - IBuiTextAreaProps defining the textarea's appearance
 * @param ref - forwarded ref to the underlying antd TextArea instance
 * @returns JSX.Element representing the textarea
 * @example
 * <BuiTextArea value={value} onChange={handleChange} autoSize={{ minRows: 2, maxRows: 12 }} />
 */
export const BuiTextArea = forwardRef<TextAreaRef, IBuiTextAreaProps>(
  ({ className, ...props }, ref) => {
    return (
      <Input.TextArea
        {...props}
        className={cn("bui-text-area", className)}
        ref={ref}
      />
    );
  },
);

BuiTextArea.displayName = "BuiTextArea";
